// Which pipeline a bundle needs, decided from the manifest before anything downloads.
// Notes: ~/comments/aos-threejs-poc/pipelineKind_resolvePipelineKind_three_answers.md

import { liveRigBranches } from "../assets/sceneManifest.ts";
import type { SceneManifest } from "../assets/sceneManifest.ts";
import type { ResolvedRig } from "../inference/rigMode.ts";

/** `live-rig` re-decodes on every rig push · `part-rig` decodes once and re-lifts · `mesh-only` has no decoders. */
export type PipelineKind = "live-rig" | "part-rig" | "mesh-only";

export interface PipelineKindInput {
  /** null for a bundle with no scene.json — a mesh-only or schema-1 layout. */
  manifest: SceneManifest | null;
  rig: ResolvedRig;
  /** The loader classified this as rig.glb + clip GLBs and no inference assets. */
  meshOnly?: boolean;
}

export interface PipelineKindResolution {
  kind: PipelineKind;
  /** The branches that consume the live control vector — empty on a part-rig bundle. */
  liveBranches: string[];
  why: string;
}

/** One answer, from the same `liveRigBranches` rule the current path splits on. */
export function resolvePipelineKind({ manifest, rig, meshOnly = false }: PipelineKindInput): PipelineKindResolution {
  if (meshOnly) return { kind: "mesh-only", liveBranches: [], why: "rig.glb + clip GLBs, no inference assets" };
  if (!manifest) return { kind: "mesh-only", liveBranches: [], why: "no scene.json to read a branch set from" };
  const live = liveRigBranches(manifest, rig);
  if (live.length) {
    return {
      kind: "live-rig",
      liveBranches: live,
      why: manifest.bodyRig === "head"
        ? "body_rig 'head' — every branch consumes one live control vector"
        : `${live.join(", ")} carry a rig→verts step at the head's width`,
    };
  }
  return {
    kind: "part-rig",
    liveBranches: [],
    why: "no branch consumes a live control vector — the part rig supplies the vertices",
  };
}
